import { useState, useRef, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
	Home,
	Inbox,
	Users,
	Handshake,
	Briefcase,
	Drama,
	ClipboardList,
	Puzzle,
	CalendarDays,
	Beaker,
	List,
	CheckSquare2,
	SquareKanban,
	Search,
	UserSearch,
	BarChart3,
	GripVertical,
	Video,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/providers/SettingsProvider";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";
import { RecentHistoryPopover } from "./RecentHistoryPopover";
import { useOverdueTasks } from "@/hooks/tasks/useOverdueTasks";

interface NavigationProps {
	collapsed: boolean;
	orientation?: "vertical" | "horizontal";
	onNavigationClick?: () => void;
}

export const Navigation = ({ collapsed, orientation = "vertical", onNavigationClick }: NavigationProps) => {

	const { t } = useTranslation();
	const { devMode, sidebarOrder, setSidebarOrderValue } = useSettings();
	const transcriptsEnabled = useFeatureFlag("transcripts");
	const analyticsEnabled = useFeatureFlag("analytics");
	const workforceEnabled = useFeatureFlag("workforce");
	const queriesEnabled = useFeatureFlag("queries");
	const peopleSearchEnabled = useFeatureFlag("peopleSearch");
	const listsEnabled = useFeatureFlag("lists");
	const kanbanEnabled = useFeatureFlag("kanban");
	const tasksEnabled = useFeatureFlag("tasks");
	const calendarEnabled = useFeatureFlag("calendar");
	const { data: overdueTasksCount = 0 } = useOverdueTasks();

	// Drag state
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [dropIndex, setDropIndex] = useState<number | null>(null);
	const dragNodeRef = useRef<HTMLLIElement | null>(null);

	// Core reorderable items (always present)
	const coreNavItems = useMemo(() => [
		{ title: t('navigation.dashboard'), icon: Home, path: "/", entityType: null },
		{ title: t('navigation.inbox', 'Inbox'), icon: Inbox, path: "/inbox", entityType: null },
		{ title: t('navigation.candidates'), icon: Users, path: "/candidates", entityType: 'candidate' as const },
		{ title: t('navigation.companies'), icon: Briefcase, path: "/companies", entityType: 'company' as const },
		{ title: t('navigation.roles'), icon: Drama, path: "/roles", entityType: null },
		{ title: t('navigation.jobs'), icon: ClipboardList, path: "/jobs", entityType: 'job' as const },
		{ title: t('navigation.matches'), icon: Puzzle, path: "/matches", entityType: 'match' as const },
		...(workforceEnabled ? [{ title: t('navigation.workforce'), icon: Handshake, path: "/workforce", entityType: 'workforce' as const }] : []),
		...(listsEnabled ? [{ title: t('navigation.lists'), icon: List, path: "/lists", entityType: 'list' as const }] : []),
		...(kanbanEnabled ? [{ title: t('navigation.kanban'), icon: SquareKanban, path: "/kanban", entityType: null }] : []),
		...(tasksEnabled ? [{ title: t('navigation.tasks'), icon: CheckSquare2, path: "/tasks", entityType: 'task' as const }] : []),
		...(calendarEnabled ? [{ title: t('navigation.calendar'), icon: CalendarDays, path: "/calendar", entityType: 'event' as const }] : []),
		...(queriesEnabled ? [{ title: t('navigation.queries'), icon: Search, path: "/queries", entityType: 'query' as const }] : []),
		...(peopleSearchEnabled ? [{ title: t('navigation.peopleSearch'), icon: UserSearch, path: "/people-search", entityType: null }] : []),
		...(analyticsEnabled ? [{ title: t('navigation.analytics'), icon: BarChart3, path: "/analytics", entityType: null }] : []),
		...(transcriptsEnabled ? [{ title: t('navigation.transcripts', 'Transcripts'), icon: Video, path: "/transcripts", entityType: null }] : []),
	], [t, workforceEnabled, listsEnabled, kanbanEnabled, tasksEnabled, calendarEnabled, queriesEnabled, peopleSearchEnabled, analyticsEnabled, transcriptsEnabled]);

	// Conditional items — always at the bottom, not reorderable
	const conditionalItems = useMemo(() => [
		...(devMode ? [{ title: "Match Test", icon: Beaker, path: "/match-test", entityType: null }] : []),
		...(devMode ? [{ title: "Merge Demo", icon: Puzzle, path: "/merge-demo", entityType: null }] : []),
	], [devMode]);

	// Apply custom order to core items
	const orderedCoreItems = useMemo(() => sidebarOrder
		? [...coreNavItems].sort((a, b) => {
			const aIdx = sidebarOrder.indexOf(a.path);
			const bIdx = sidebarOrder.indexOf(b.path);
			// Items not in the saved order go to the end
			if (aIdx === -1 && bIdx === -1) return 0;
			if (aIdx === -1) return 1;
			if (bIdx === -1) return -1;
			return aIdx - bIdx;
		})
		: coreNavItems, [sidebarOrder, coreNavItems]);

	const navItems = useMemo(() => [...orderedCoreItems, ...conditionalItems], [orderedCoreItems, conditionalItems]);

	const canDrag = orientation === "vertical" && !collapsed;
	const corePathSet = useMemo(() => new Set(coreNavItems.map(i => i.path)), [coreNavItems]);

	const tourAttrByPath: Record<string, string> = {
		"/": "nav-dashboard",
		"/candidates": "nav-candidates",
		"/jobs": "nav-jobs",
		"/matches": "nav-matches",
	};

	const handleDragStart = useCallback((e: React.DragEvent<HTMLLIElement>, index: number) => {
		setDragIndex(index);
		dragNodeRef.current = e.currentTarget;
		e.dataTransfer.effectAllowed = "move";
		// Make the drag image slightly transparent
		requestAnimationFrame(() => {
			if (dragNodeRef.current)
				dragNodeRef.current.style.opacity = "0.4";
		});
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLLIElement>, index: number) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		if (dragIndex === null || index === dragIndex) {
			setDropIndex(null);
			return;
		}
		// Only allow dropping on core items
		if (!corePathSet.has(navItems[index]?.path)) {
			setDropIndex(null);
			return;
		}
		setDropIndex(index);
	}, [dragIndex, navItems, corePathSet]);

	const handleDrop = useCallback((e: React.DragEvent<HTMLLIElement>, targetIndex: number) => {
		e.preventDefault();
		if (dragIndex === null || dragIndex === targetIndex) return;
		// Only reorder within core items
		if (!corePathSet.has(navItems[targetIndex]?.path)) return;

		const newOrder = orderedCoreItems.map(i => i.path);
		const [moved] = newOrder.splice(dragIndex, 1);
		// Clamp target to core items range
		const clampedTarget = Math.min(targetIndex, orderedCoreItems.length - 1);
		newOrder.splice(clampedTarget, 0, moved);
		setSidebarOrderValue(newOrder);

		setDragIndex(null);
		setDropIndex(null);
	}, [dragIndex, navItems, orderedCoreItems, corePathSet, setSidebarOrderValue]);

	const handleDragEnd = useCallback(() => {
		if (dragNodeRef.current)
			dragNodeRef.current.style.opacity = "1";
		setDragIndex(null);
		setDropIndex(null);
		dragNodeRef.current = null;
	}, []);

	return (
		<nav className={cn(
			"flex-1 overflow-y-auto py-2",
			orientation === "horizontal" && "flex-1 flex items-center justify-center py-0"
		)}>
			<ul className={cn(
				"space-y-0.5 px-3",
				orientation === "horizontal" && "flex space-y-0 space-x-1 px-0"
			)}>
				{navItems.map((item, index) => {
					const isCoreItem = corePathSet.has(item.path);
					const isDraggable = canDrag && isCoreItem;
					const isDropTarget = dropIndex === index && dragIndex !== null;

					return (
						<li
							key={item.path}
							className={cn(
								orientation === "horizontal" && "flex-1",
								isDropTarget && "border-t-2 border-blue-500",
							)}
							draggable={isDraggable}
							onDragStart={isDraggable ? (e) => handleDragStart(e, index) : undefined}
							onDragOver={isDraggable ? (e) => handleDragOver(e, index) : undefined}
							onDrop={isDraggable ? (e) => handleDrop(e, index) : undefined}
							onDragEnd={isDraggable ? handleDragEnd : undefined}
						>
							<div className={cn(
								"relative group",
								orientation === "vertical" && "flex items-center"
							)}>
								{/* Drag handle — visible on hover for core items */}
								{isDraggable && (
									<div className="absolute -left-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-60 transition-opacity cursor-grab z-10">
										<GripVertical size={14} className="text-muted-foreground" />
									</div>
								)}
								<NavLink
									to={item.path}
									className={({ isActive }) => cn(
										"sidebar-item",
										isActive && "sidebar-item-active",
										orientation === "horizontal" && "flex-col items-center justify-center px-1 py-1 text-xs gap-1",
										orientation === "vertical" && "flex-1",
										item.entityType && orientation === "vertical" && "pr-2"
									)}
									title={item.title}
									onClick={onNavigationClick}
									data-tour={tourAttrByPath[item.path]}
								>
									<item.icon size={18} />
									{!collapsed && orientation !== "horizontal" && (
										<div className="flex items-center justify-between flex-1">
											<span>{item.title}</span>
											{item.path === "/tasks" && overdueTasksCount > 0 && (
												<div className="w-2 h-2 bg-red-500 rounded-full ml-2" />
											)}
										</div>
									)}
									{orientation === "horizontal" && <span className="hidden sm:block truncate">{item.title}</span>}
								</NavLink>
								{item.entityType && orientation === "vertical" && !collapsed && (
									<div className="absolute right-0.5 top-1/2 -translate-y-1/2">
										<RecentHistoryPopover entityType={item.entityType} />
									</div>
								)}
							</div>
						</li>
					);
				})}
			</ul>
		</nav>
	);
};
